import { System } from './../../shared-components/System';
import { Component, EventEmitter, OnInit } from '@angular/core';
import { UserService } from '../../shared-components/user/user.service';
import { YearService } from '../../shared-components/year/year.service';
import { User } from '../../shared-components/user/user.model';
import { Year } from '../../shared-components/year/year.model';
import { AnnouncementService } from './announcement.service';
import { Announcement } from './announcement.model';
import { StudentService } from '../../shared-components/student/student.service';
import { MatDialog } from '@angular/material/dialog';
import { SharedService } from '../../shared-components/shared.service';
import {forkJoin, Observable, Subscription} from 'rxjs';
import { AnnouncementMessageComponent } from './announcement-message/announcement-message.component';
import { TylerMatTableDataSource } from '../../shared-components/angular-material/extended.class';
import { LoginService } from '../login/login.service';
import { Router } from '@angular/router';
import {Enrollment} from '../../shared-components/student/enrollment.model';

// Class
@Component({
    selector: 'announcement',
    templateUrl: './announcement.component.html',
    styleUrls: [
        './announcement.component.css'
    ]
})
export class AnnouncementComponent implements OnInit {

    // Properties
    currentUser: User;
    defaultYear: Year;
    inputDate: Date;
    inputString: string;
    announcements: Announcement[] = [];
    districtMessages: Announcement[] = [];
    schoolNames: string[] = [];
    schoolExpanded: {[name: string]: boolean} = {};
    gotAnnouncements = false;
    noAnnouncements = false;
    toggleAllRows: EventEmitter<boolean> = new EventEmitter<boolean>();
    isBusy: boolean;
    subscriptions: Subscription[] = [];
    v10: boolean;
    tableData: TylerMatTableDataSource[] = [];
    showTableFlags = [false, false, false];
    sectionNames = ['District Announcements', 'School Announcements', 'Class Announcements'];
    student360 = false;
    studentEnrollments: Enrollment[] = [];

    // Class Functions
    constructor(
        private userService: UserService,
        private yearService: YearService,
        private loginService: LoginService,
        private announcementService: AnnouncementService,
        private studentService: StudentService,
        private sharedService: SharedService,
        private dialog: MatDialog,
        private router: Router
    ) {

    }

    // Event Functions
    ngOnInit() {
        // POST: initializes the services and sets input date to the current date. Sets input string accordingly
        this.v10 = this.loginService.LoginSettings.V10;
        this.currentUser = this.userService.currentUser;
        this.defaultYear = this.yearService.defaultYear;
        this.inputDate = new Date();
        this.inputString = this.inputDate.toLocaleDateString();
        this.student360 = this.router.url.toLowerCase().indexOf('/student360') >= 0;
        this.getAnnouncements();
        if (this.v10) {
            this.subscriptions.push(
                this.yearService.observableDefaultYearStream$.subscribe(() => {
                    this.getAnnouncements();
                }),
                this.studentService.selectedStudentStream$.subscribe(() => {
                    this.noAnnouncements = false;
                    this.getAnnouncements();
                }),
                this.sharedService.observableIsPageLoadedStream$.subscribe(loaded => {
                    this.isBusy = !loaded;
                })
            );
        }
        else {
            if (this.sharedService.currentPortal == undefined) {
                this.subscriptions.push(
                    this.yearService.observableDefaultYearStream$.subscribe(() => {
                        this.getAnnouncements();
                    }),
                    this.studentService.selectedStudentStream$.subscribe(() => {
                        this.noAnnouncements = false;
                        this.getAnnouncements();
                    }),
                    this.sharedService.observableIsPageLoadedStream$.subscribe(loaded => {
                        this.isBusy = !loaded;
                    })
                );
            }
        }
        this.subscriptions.push(
            this.toggleAllRows.subscribe(toggle => {
                this.tableData[0].expandAll(toggle);
                this.tableData[1].expandAll(toggle);
                this.tableData[2].expandAll(toggle);
                this.schoolNames.forEach(schoolName => {
                    this.schoolExpanded[schoolName] = toggle;
                });
            })
        );
    }

    // Methods
    getAnnouncements() {
        // POST: gets the announcements for the date input
        this.gotAnnouncements = false;
        if (this.inputDate != null && this.inputDate.toString() != 'Invalid Date') {
            if (this.currentUser.UserType == 1 &&
                (!this.sharedService.currentPortal || !this.sharedService.currentPortal.applyFilterStudents)) {
                if (this.student360) {
                    this.subscriptions.push(
                        this.announcementService.getStudentAnnouncements(this.inputDate).subscribe(data => {
                            this.announcements = [];
                            data.forEach(datum => {
                                this.announcements.push(System.convert<Announcement>(datum, new Announcement()));
                            });
                            this.virtualize();
                        })
                    );
                }
                else {
                    this.subscriptions.push(
                        this.announcementService.getStaffAnnouncements(this.inputDate).subscribe(data => {
                            this.announcements = [];
                            data.forEach(datum => {
                                this.announcements.push(System.convert<Announcement>(datum, new Announcement()));
                            });
                            this.virtualize();
                        })
                    );
                }
            } else {
                let observable: Observable<any> = this.announcementService.getStudentAnnouncements(this.inputDate);
                if (this.student360) {
                    observable = forkJoin(
                        observable,
                        this.studentService.getStudentEnrollments(
                            this.studentService.selectedStudent.Id,
                            this.yearService.defaultYear.Id
                        )
                    );
                }
                this.subscriptions.push(
                    observable.subscribe(data => {
                        this.announcements = [];
                        const announcementData = this.student360 ? data[0] : data;
                        announcementData.forEach(datum => {
                            this.announcements.push(System.convert<Announcement>(datum, new Announcement()));
                        });
                        if (this.student360) {
                            this.studentEnrollments = data[1];
                        }
                        this.virtualize();
                    })
                );
            }
            this.gotAnnouncements = true;
        }
        if (!this.gotAnnouncements) {
            this.announcements = [];
            this.virtualize();
        }
    }

    virtualize() {
        this.noAnnouncements = false;
        // POST: transforms announcements into grid data

        // District student announcements
        const districtData = this.filterAnnouncements(this.announcements, 'D');
        this.tableData[0] = new TylerMatTableDataSource(districtData);
        this.showTableFlags[0] = districtData.length > 0;

        // Class staff announcements
        const classData = this.filterAnnouncements(this.announcements, 'C');
        this.tableData[2] = new TylerMatTableDataSource(classData);
        this.showTableFlags[2] = classData.length > 0;

        // District Message
        this.districtMessages = this.filterAnnouncements(this.announcements, 'M');

        // School staff announcements
        const schoolData = this.filterAnnouncements(this.announcements, 'S');
        this.schoolNames = [];
        schoolData.forEach(obj => {
            if (!this.schoolNames.includes(obj.SchoolName)) {
                this.schoolNames.push(obj.SchoolName);
            }
            this.schoolExpanded[obj.SchoolName] = true;
        });
        if (!this.student360) {
            this.schoolNames.sort((a, b) =>
                a.localeCompare(b)
            );
        } else {
            const primaryEnrollment = this.studentEnrollments.find(o => o.PrimaryEnrollment);
            this.schoolNames.sort((a, b) => {
                if (a == primaryEnrollment.SchoolName && b != primaryEnrollment.SchoolName) {
                    return -1;
                } else if (b == primaryEnrollment.SchoolName && a != primaryEnrollment.SchoolName) {
                    return 1;
                } else {
                    return a.localeCompare(b);
                }
            });
        }
        this.tableData[1] = new TylerMatTableDataSource(schoolData);
        this.showTableFlags[1] = schoolData.length > 0;

        // Determining whether to show any announcements
        this.noAnnouncements = !this.showTableFlags[0] && !this.showTableFlags[1] && !this.showTableFlags[2];
    }

    private filterAnnouncements(announcements: Announcement[], type: string) {
        // PRE: a list of announcements and a single character type indicator
        // POST: filters the announcements for a given type. Returns those announcements whose type matches.
        return announcements.filter(function (announcement: Announcement) {
            return announcement.AnnouncementType == type;
        });
    }

    showDistrictMessages() {
        // POST: shows the district message
        const dialogBox = this.dialog.open(AnnouncementMessageComponent, {
            data: {
                districtMessages: this.districtMessages
            },
            disableClose: true
        });
        dialogBox.componentInstance.dialogBox = dialogBox;
    }

    studentInfoView() {
        return this.sharedService.currentPortal && this.sharedService.currentPortal.applyFilterStudents;
    }

    showYearSelector() {
        return !this.v10;
    }
}
